/**
 * /account/settings — unified customer settings hub with 6 tabs.
 *
 * The shell owns the tab nav + route param sync (?tab=profile etc.);
 * each tab is a thin focused component under pages/account-settings/.
 * Built per the user's brief: tabs, soft-delete with 30-day recovery,
 * NO 2FA, NO profile photo. Tier lock-outs propagate through the
 * Notifications tab.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import {
  CircleUser, Bell, Settings, Shield, CreditCard, ShieldOff, MessageSquare, Gift,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ProfileTab from './account-settings/ProfileTab';
import NotificationsTab from './account-settings/NotificationsTab';
import PreferencesTab from './account-settings/PreferencesTab';
import SecurityTab from './account-settings/SecurityTab';
import BillingTab from './account-settings/BillingTab';
import PrivacyTab from './account-settings/PrivacyTab';
import MessagesTab from './account-settings/MessagesTab';
import ReferralsTab from './account-settings/ReferralsTab';
import PageTitle from '../components/PageTitle';

const TABS = [
  { key: 'profile', label: 'Profile', Icon: CircleUser, Component: ProfileTab },
  { key: 'messages', label: 'Messages', Icon: MessageSquare, Component: MessagesTab },
  { key: 'notifications', label: 'Notifications', Icon: Bell, Component: NotificationsTab },
  { key: 'preferences', label: 'Preferences', Icon: Settings, Component: PreferencesTab },
  { key: 'security', label: 'Security', Icon: Shield, Component: SecurityTab },
  { key: 'billing', label: 'Billing', Icon: CreditCard, Component: BillingTab },
  { key: 'referrals', label: 'Refer & earn', Icon: Gift, Component: ReferralsTab },
  { key: 'privacy', label: 'Privacy & data', Icon: ShieldOff, Component: PrivacyTab },
];

const AccountSettings = () => {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const initial = params.get('tab') || 'profile';
  const [active, setActive] = useState(TABS.some((t) => t.key === initial) ? initial : 'profile');

  // Keep the URL in sync so deep links / refreshes land on the right tab.
  useEffect(() => {
    if (active !== params.get('tab')) {
      params.set('tab', active);
      setParams(params, { replace: true });
    }
  }, [active, params, setParams]);

  const ActiveComponent = useMemo(
    () => TABS.find((t) => t.key === active)?.Component || ProfileTab,
    [active],
  );

  if (!user) return <Navigate to="/login" replace />;

  // Gate the tab panels on auth being hydrated — without this, child tab
  // components fire GET /account/* before the access_token cookie is
  // attached on first paint, producing harmless-but-noisy 401s in dev
  // consoles. Once `user` is present, the cookie is in flight.
  return (
    <div className="p-6 sm:p-8 max-w-6xl mx-auto" data-testid="account-settings-page">
      <PageTitle
        title="Account settings"
        subtitle="Profile, notifications, billing, and account controls — all in one place."
        testId="account-settings-title"
        className="mb-8"
      />

      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-6">
        {/* Mobile: dropdown. Desktop: vertical nav. */}
        <div className="md:hidden">
          <select
            value={active}
            onChange={(e) => setActive(e.target.value)}
            className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm"
            data-testid="account-settings-mobile-select"
          >
            {TABS.map((t) => (
              <option key={t.key} value={t.key}>{t.label}</option>
            ))}
          </select>
        </div>
        <nav className="hidden md:flex md:flex-col gap-1" data-testid="account-settings-tabs">
          {TABS.map((t) => {
            const isActive = active === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setActive(t.key)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left ${
                  isActive
                    ? 'bg-[#EFF6FF] text-[#0052FF]'
                    : 'text-[#475569] hover:bg-[#F8FAFC] hover:text-[#020617]'
                }`}
                data-testid={`account-settings-tab-${t.key}`}
              >
                <t.Icon className="w-4 h-4 flex-shrink-0" />
                {t.label}
              </button>
            );
          })}
        </nav>

        <div data-testid={`account-settings-panel-${active}`}>
          <ActiveComponent />
        </div>
      </div>
    </div>
  );
};

export default AccountSettings;
